import { cx } from '@linaria/core';
import { useRef } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { avoidBreakAfter, avoidBreakInside } from '@provider/print';
import { units, breakpoints } from '@ui/css';
import { Tooltip } from '@ui/kit';
import { hasOpgavekompleksetTaskInputs, canValidateOpgavekompleksetTask } from '@utils/component-framework';
import { useKatex, ShowFor, SizeTransition, TruncateLines, Chevron } from '@utils/react';
import { useTaskContext } from '../../components/organism.task/context.js';
import { useOpgavekompleksetComponent } from '../../state/context.data.js';
import { useOpgavekompleksetEntry } from '../../state/hook.entry.js';
import { shouldShowReport } from '../../state/util.should-show-report.js';
import { getTaskTitle } from '../../utils/get-task-title.js';
import { StatusIndicator } from './atom.indicator.js';
import { Stats } from './atom.stats.js';
import { container, titleContainer, title, status, stats, summary, titleTransitionDurationMs, didAnswer, chevron, mobileTopContainer, mobileBottomContainer } from './molecule.summary.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';

function TaskAccordionSummary(props) {
  const ref = useRef(null);
  const taskEntry = useOpgavekompleksetEntry({
    select: entry => entry?.data.tasks[props.task.id]
  });
  const isSkipped = taskEntry?.state.skipped ?? true;
  const onClick = evt => {
    evt.preventDefault();
    props.onToggle();
  };
  useKatex(ref, [props.expanded]);
  return /*#__PURE__*/jsxs("summary", {
    ref: ref,
    className: summary,
    onClick: onClick,
    "data-skipped": isSkipped,
    "data-expanded": props.expanded,
    children: [/*#__PURE__*/jsx(ShowFor, {
      breakpoint: breakpoints.tablet.andUp(),
      children: /*#__PURE__*/jsx(DesktopLayout, {
        ...props
      })
    }), /*#__PURE__*/jsx(ShowFor, {
      breakpoint: breakpoints.mobile,
      children: /*#__PURE__*/jsx(MobileLayout, {
        ...props
      })
    })]
  });
}
function DesktopLayout(props) {
  const component = useOpgavekompleksetComponent();
  const taskEntry = useOpgavekompleksetEntry({
    select: entry => entry?.data.tasks[props.task.id]
  });
  const isSkipped = taskEntry?.state.skipped ?? true;
  const canValidate = canValidateOpgavekompleksetTask(props.task);
  const showStats = (isSkipped || canValidate) && shouldShowReport(component.data.settings.flowType);
  const hasInputs = hasOpgavekompleksetTaskInputs(props.task);
  return /*#__PURE__*/jsxs("div", {
    className: container,
    children: [/*#__PURE__*/jsx(TransitionGroup, {
      className: titleContainer,
      children: props.expanded ? /*#__PURE__*/jsx(SizeTransition, {
        axis: "vertical",
        swappable: true,
        classNames: title,
        timeout: titleTransitionDurationMs,
        delayMs: {
          enter: 10,
          exit: 0
        },
        children: ref => /*#__PURE__*/jsxs("div", {
          ref: ref,
          className: title,
          children: ["Opgave ", props.index + 1, ":", " ", /*#__PURE__*/jsx("strong", {
            children: getTaskTitle(props.task)
          })]
        })
      }, "expanded") : /*#__PURE__*/jsx(SizeTransition, {
        axis: "vertical",
        swappable: true,
        classNames: title,
        timeout: titleTransitionDurationMs,
        children: ref => /*#__PURE__*/jsx("div", {
          ref: ref,
          className: title,
          children: /*#__PURE__*/jsxs(TruncateLines, {
            maxLines: 2,
            children: ["Opgave ", props.index + 1, ":", " ", /*#__PURE__*/jsx("strong", {
              children: getTaskTitle(props.task)
            })]
          })
        })
      }, "collapsed")
    }), /*#__PURE__*/jsx(StatusIndicator, {
      className: status,
      task: props.task
    }), showStats ? /*#__PURE__*/jsx(Stats, {
      className: stats,
      correct: taskEntry?.stats.correct ?? 0,
      total: taskEntry?.stats.total ?? 0,
      skipped: isSkipped
    }) : /*#__PURE__*/jsx("span", {
      className: cx(stats, didAnswer),
      "data-mixed": shouldShowReport(component.data.settings.flowType),
      children: hasInputs ? isSkipped ? "Ikke besvaret" : "Besvaret" : isSkipped ? "Ikke set" : "Set"
    }), /*#__PURE__*/jsx(Tooltip, {
      ariaType: undefined,
      placement: "top",
      disabled: isSkipped,
      body: props.expanded ? "Skjul resultat" : "Vis opgaveresultat",
      children: ref => /*#__PURE__*/jsx("span", {
        ref: ref,
        className: chevron,
        children: /*#__PURE__*/jsx(Chevron, {
          vertical: true,
          inversed: props.expanded
        })
      })
    })]
  });
}
function MobileLayout(props) {
  const component = useOpgavekompleksetComponent();
  const taskEntry = useOpgavekompleksetEntry({
    select: entry => entry?.data.tasks[props.task.id]
  });
  const isSkipped = taskEntry?.state.skipped ?? true;
  const hasInputs = hasOpgavekompleksetTaskInputs(props.task);
  const canValidate = canValidateOpgavekompleksetTask(props.task);
  const showStats = (isSkipped || canValidate) && shouldShowReport(component.data.settings.flowType);
  return /*#__PURE__*/jsxs("div", {
    className: container,
    children: [/*#__PURE__*/jsxs("div", {
      className: mobileTopContainer,
      children: [/*#__PURE__*/jsx(TransitionGroup, {
        className: titleContainer,
        children: props.expanded ? /*#__PURE__*/jsx(SizeTransition, {
          axis: "vertical",
          swappable: true,
          classNames: title,
          timeout: titleTransitionDurationMs,
          children: ref => /*#__PURE__*/jsxs("div", {
            ref: ref,
            className: title,
            children: ["Opgave ", props.index + 1, ":", " ", /*#__PURE__*/jsx("strong", {
              children: getTaskTitle(props.task)
            })]
          })
        }, "expanded") : /*#__PURE__*/jsx(SizeTransition, {
          axis: "vertical",
          swappable: true,
          classNames: title,
          timeout: titleTransitionDurationMs,
          children: ref => /*#__PURE__*/jsx("div", {
            ref: ref,
            className: title,
            children: /*#__PURE__*/jsxs(TruncateLines, {
              maxLines: 2,
              children: ["Opgave ", props.index + 1, ":", " ", /*#__PURE__*/jsx("strong", {
                children: getTaskTitle(props.task)
              })]
            })
          })
        }, "collapsed")
      }), /*#__PURE__*/jsx(StatusIndicator, {
        className: status,
        task: props.task
      }), /*#__PURE__*/jsx(Tooltip, {
        ariaType: undefined,
        placement: "top",
        disabled: isSkipped,
        body: props.expanded ? "Skjul resultat" : "Vis opgaveresultat",
        children: ref => /*#__PURE__*/jsx("span", {
          ref: ref,
          className: chevron,
          children: /*#__PURE__*/jsx(Chevron, {
            vertical: true,
            inversed: props.expanded
          })
        })
      })]
    }), /*#__PURE__*/jsx("div", {
      className: mobileBottomContainer,
      children: showStats ? /*#__PURE__*/jsx(Stats, {
        className: stats,
        correct: taskEntry?.stats.correct ?? 0,
        total: taskEntry?.stats.total ?? 0,
        skipped: isSkipped
      }) : /*#__PURE__*/jsx("span", {
        className: cx(stats, didAnswer),
        "data-mixed": shouldShowReport(component.data.settings.flowType),
        children: hasInputs ? isSkipped ? "Ikke besvaret" : "Besvaret" : isSkipped ? "Ikke set" : "Set"
      })
    })]
  });
}
function PrintTaskSummary(props) {
  const ref = useRef(null);
  const component = useOpgavekompleksetComponent();
  const context = useTaskContext();
  const taskEntry = useOpgavekompleksetEntry({
    select: entry => entry?.data.tasks[props.task.id]
  });
  const isSkipped = taskEntry?.state.skipped ?? true;
  const hasInputs = hasOpgavekompleksetTaskInputs(props.task);
  const canValidate = canValidateOpgavekompleksetTask(props.task);
  const showStats = context !== "input" && (isSkipped || canValidate) && shouldShowReport(component.data.settings.flowType);
  useKatex(ref);
  return /*#__PURE__*/jsxs("div", {
    ref: ref,
    className: container,
    "data-type": "print",
    ...avoidBreakInside(),
    ...(props.avoidBreakAfter ? avoidBreakAfter() : undefined),
    children: [/*#__PURE__*/jsx("div", {
      className: titleContainer,
      children: /*#__PURE__*/jsxs("div", {
        className: title,
        children: ["Opgave ", props.index + 1, ": ", /*#__PURE__*/jsx("strong", {
          children: getTaskTitle(props.task)
        })]
      })
    }), /*#__PURE__*/jsx(StatusIndicator, {
      className: status,
      task: props.task
    }), showStats ? /*#__PURE__*/jsx(Stats, {
      className: stats,
      correct: taskEntry?.stats.correct ?? 0,
      total: taskEntry?.stats.total ?? 0,
      skipped: isSkipped
    }) : /*#__PURE__*/jsx("span", {
      className: stats,
      style: {
        width: units.px(124)
      },
      children: hasInputs ? isSkipped ? "Ikke besvaret" : context === "input" ? "Igangværende" : "Besvaret" : isSkipped ? "Ikke set" : "Set"
    })]
  });
}

export { PrintTaskSummary, TaskAccordionSummary };
//# sourceMappingURL=molecule.summary.js.map
